import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import AppShell from '../components/layout/AppShell';
import { useTranslation } from 'react-i18next';
import { useToast } from '../context/ToastContext';
import { studentAPI } from '../utils/api';

const QuizPage = () => {
  const { topicId } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { success, error } = useToast();

  const getSubjectFromTopicId = (id) => {
    if (!id) return 'science';
    const prefix = id.split('_')[0];
    const map = { sci: 'science', mth: 'mathematics', sst: 'social_science', eng: 'english', hin: 'hindi', ai: 'ai_ct' };
    return map[prefix] || 'science';
  };

  const [quiz, setQuiz] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [currentQ, setCurrentQ] = useState(0);
  const [answers, setAnswers] = useState({});
  const [revealed, setRevealed] = useState({});
  const [results, setResults] = useState(null);
  const [quizDone, setQuizDone] = useState(false);

  useEffect(() => { loadQuiz(); }, [topicId]);

  const loadQuiz = async () => {
    setLoading(true);
    setAnswers({});
    setRevealed({});
    setResults(null);
    setQuizDone(false);
    setCurrentQ(0);
    try {
      const res = await studentAPI.getQuiz(topicId);
      setQuiz(res.data.data);
    } catch {
      error('Quiz not available for this topic yet.');
      navigate(`/study/${topicId}`);
    } finally {
      setLoading(false);
    }
  };

  const handleAnswer = (questionId, option) => {
    if (revealed[questionId]) return;
    setAnswers(prev => ({ ...prev, [questionId]: option }));
  };

  const handleNext = (questionId) => {
    if (!answers[questionId]) return error('Please select an answer first.');
    setRevealed(prev => ({ ...prev, [questionId]: true }));
    if (currentQ < quiz.questions.length - 1) {
      setCurrentQ(currentQ + 1);
    }
  };

  const handleSubmit = async () => {
    const unanswered = quiz.questions.filter(q => !answers[q.question_id]);
    if (unanswered.length > 0) return error(`Please answer all ${unanswered.length} remaining questions.`);
    setSubmitting(true);
    try {
      const answersArray = Object.entries(answers).map(([question_id, selected_option]) => ({ question_id, selected_option }));
      const res = await studentAPI.submitQuiz(topicId, { set_id: quiz.set_id, answers: answersArray });
      setResults(res.data.data);
      setQuizDone(true);
      success('Quiz submitted!');
    } catch {
      error('Failed to submit quiz. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const getOptionStyle = (questionId, option) => {
    const selected = answers[questionId] === option;
    const isRevealed = revealed[questionId];
    const question = quiz.questions.find(q => q.question_id === questionId);
    const isCorrect = option === question?.correct_option;
    if (isRevealed) {
      if (isCorrect) return { background: '#E6F4EC', border: '2px solid #1B7F3E', color: '#1B7F3E' };
      if (selected && !isCorrect) return { background: '#FEE2E2', border: '2px solid #DC2626', color: '#DC2626' };
      return { background: '#F7F8FC', border: '1.5px solid #E2E8F0', color: '#94A3B8' };
    }
    if (selected) return { background: '#FFF3E0', border: '2px solid #FF6B00', color: '#FF6B00' };
    return { background: 'white', border: '1.5px solid #E2E8F0', color: 'var(--text)' };
  };

  if (loading) return <AppShell><div style={{ textAlign: 'center', padding: '60px' }}><div className="spinner" style={{ margin: '0 auto' }}></div></div></AppShell>;
  if (!quiz) return null;

  const totalQ = quiz.questions.length;
  const revealedCount = Object.keys(revealed).length;
  const isLastQuestion = currentQ === totalQ - 1;
  const currentQuestion = quiz.questions[currentQ];
  const currentAnswered = answers[currentQuestion?.question_id];
  const currentRevealed = revealed[currentQuestion?.question_id];
  const allRevealed = revealedCount === totalQ;

  if (quizDone && results) {
    const pct = results.score;
    const color = pct >= 80 ? '#1B7F3E' : pct >= 50 ? '#B45309' : '#DC2626';
    return (
      <AppShell>
        <div className="topbar">
          <div style={{ fontSize: '13px', fontWeight: 700 }}>Results — {quiz.topic_name}</div>
        </div>
        <div className="page-content" style={{ maxWidth: '760px', margin: '0 auto' }}>
          <div className="card fade-up-1" style={{ textAlign: 'center', padding: '40px 32px', marginBottom: '16px' }}>
            <div style={{ fontSize: '64px', fontWeight: 900, color, marginBottom: '8px' }}>{pct}%</div>
            <div style={{ fontSize: '18px', fontWeight: 800, marginBottom: '6px' }}>
              {pct >= 80 ? '🎉 Excellent!' : pct >= 50 ? '👍 Good effort!' : '💪 Keep practicing!'}
            </div>
            <div style={{ fontSize: '14px', color: 'var(--muted)', marginBottom: '24px' }}>{results.correct} out of {results.total} correct</div>
            {results.revision_scheduled && (
              <div style={{ background: '#E8F0FB', borderRadius: '10px', padding: '14px 20px', marginBottom: '20px', fontSize: '13px', color: 'var(--brand-secondary)', fontWeight: 700 }}>
                ✅ 1-4-7 revision scheduled! Day 1, Day 4 and Day 7 reminders set.
              </div>
            )}
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'center', flexWrap: 'wrap' }}>
              <button onClick={loadQuiz} style={{ background: 'var(--brand-secondary)', color: 'white', border: 'none', borderRadius: '8px', padding: '11px 20px', fontSize: '13px', fontWeight: 800, cursor: 'pointer' }}>🔄 Try Again</button>
              <button onClick={() => navigate(`/study/${topicId}`)} style={{ background: 'white', color: 'var(--text)', border: '1.5px solid var(--border)', borderRadius: '8px', padding: '11px 20px', fontSize: '13px', fontWeight: 700, cursor: 'pointer' }}>📖 Back to Study</button>
              <button onClick={() => navigate(`/subjects/${getSubjectFromTopicId(topicId)}`)} style={{ background: 'var(--brand-primary)', color: 'white', border: 'none', borderRadius: '8px', padding: '11px 20px', fontSize: '13px', fontWeight: 800, cursor: 'pointer' }}>📚 Continue Studying</button>
            </div>
          </div>
          <div className="card fade-up-2" style={{ padding: '20px' }}>
            <div style={{ fontSize: '14px', fontWeight: 800, marginBottom: '16px' }}>Answer Breakdown</div>
            {results.results?.map((r, i) => (
              <div key={r.question_id} style={{ marginBottom: '16px', paddingBottom: '16px', borderBottom: i < results.results.length - 1 ? '1px solid var(--border)' : 'none' }}>
                <div style={{ display: 'flex', gap: '8px', marginBottom: '8px', flexWrap: 'wrap' }}>
                  <span style={{ fontSize: '11px', fontWeight: 800, padding: '2px 8px', borderRadius: '20px', background: r.is_correct ? '#E6F4EC' : '#FEE2E2', color: r.is_correct ? '#1B7F3E' : '#DC2626', flexShrink: 0 }}>
                    {r.is_correct ? '✓ Correct' : '✕ Wrong'}
                  </span>
                  <span style={{ fontSize: '13px', fontWeight: 700 }}>Q{i + 1}. {r.question}</span>
                </div>
                {!r.is_correct && (
                  <div style={{ fontSize: '12px', color: '#1B7F3E', background: '#E6F4EC', borderRadius: '6px', padding: '6px 10px', marginBottom: '6px', fontWeight: 600 }}>
                    ✓ Correct answer: {r.correct_option}
                  </div>
                )}
                <div style={{ fontSize: '12px', color: 'var(--muted)', background: 'var(--bg)', borderRadius: '6px', padding: '8px 10px', lineHeight: 1.5 }}>
                  💡 {r.explanation}
                </div>
              </div>
            ))}
          </div>
        </div>
      </AppShell>
    );
  }

  return (
    <AppShell>
      <div className="topbar">
        <button onClick={() => navigate(`/study/${topicId}`)} style={{ background: 'none', border: 'none', fontSize: '13px', fontWeight: 700, color: 'var(--brand-primary)', cursor: 'pointer' }}>← Back to Study</button>
        <div style={{ fontSize: '13px', fontWeight: 700, color: 'var(--muted)' }}>{quiz.topic_name} — Quiz</div>
        <div style={{ fontSize: '12px', color: 'var(--muted)' }}>No time limit · Take your time</div>
      </div>
      <div className="page-content" style={{ maxWidth: '760px', margin: '0 auto' }}>
        <div className="card fade-up-1" style={{ padding: '20px 24px', marginBottom: '14px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
            <div>
              <div style={{ fontSize: '16px', fontWeight: 800 }}>✏️ {quiz.topic_name} — Quiz</div>
              <div style={{ fontSize: '12px', color: 'var(--muted)', marginTop: '2px' }}>Unique questions every attempt · Answers revealed after each question</div>
            </div>
            <div style={{ fontSize: '13px', fontWeight: 800, background: 'var(--brand-secondary-light)', color: 'var(--brand-secondary)', borderRadius: '20px', padding: '4px 12px', whiteSpace: 'nowrap' }}>
              Question {currentQ + 1} of {totalQ}
            </div>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', color: 'var(--muted)', marginBottom: '6px', fontWeight: 600 }}>
            <span>Quiz Progress</span>
            <span style={{ color: allRevealed ? 'var(--green)' : 'var(--brand-primary)', fontWeight: 800 }}>{Math.round((revealedCount / totalQ) * 100)}% done</span>
          </div>
          <div style={{ background: 'var(--border)', borderRadius: '10px', height: '6px', marginBottom: '12px' }}>
            <div style={{ background: allRevealed ? 'var(--green)' : 'var(--brand-primary)', borderRadius: '10px', height: '100%', width: `${(revealedCount / totalQ) * 100}%`, transition: 'width 0.3s' }}></div>
          </div>
          <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
            {quiz.questions.map((q, idx) => {
              const isAnswered = !!answers[q.question_id];
              const isRevealed = !!revealed[q.question_id];
              const isCurrent = idx === currentQ;
              const isCorrect = isRevealed && answers[q.question_id] === q.correct_option;
              return (
                <div key={idx} onClick={() => setCurrentQ(idx)}
                  style={{ width: '32px', height: '32px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px', fontWeight: 800, cursor: 'pointer',
                    background: isCurrent ? 'var(--brand-primary)' : isRevealed ? (isCorrect ? '#1B7F3E' : '#DC2626') : isAnswered ? '#FFF3E0' : 'var(--bg)',
                    color: isCurrent || isRevealed ? 'white' : isAnswered ? '#FF6B00' : 'var(--muted)',
                    border: isCurrent ? 'none' : isAnswered && !isRevealed ? '2px solid #FF6B00' : '1.5px solid var(--border)' }}>
                  {idx + 1}
                </div>
              );
            })}
          </div>
        </div>

        <div className="card fade-up-2" style={{ padding: '28px 28px 24px', marginBottom: '14px' }}>
          <div style={{ fontSize: '11px', fontWeight: 800, color: 'var(--brand-primary)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '12px' }}>
            Question {currentQ + 1} of {totalQ}
          </div>
          <div style={{ fontSize: '16px', fontWeight: 700, color: 'var(--text)', lineHeight: 1.6, marginBottom: '24px' }}>
            {currentQuestion.question}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {currentQuestion.options.map((option, oi) => {
              const optLabel = ['A', 'B', 'C', 'D'][oi];
              const style = getOptionStyle(currentQuestion.question_id, option);
              const isSelected = answers[currentQuestion.question_id] === option;
              return (
                <div key={oi} onClick={() => handleAnswer(currentQuestion.question_id, option)}
                  style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '13px 16px', borderRadius: '10px', cursor: currentRevealed ? 'default' : 'pointer', transition: 'all 0.15s', ...style }}>
                  <div style={{ width: '28px', height: '28px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px', fontWeight: 800, flexShrink: 0,
                    background: isSelected ? (style.color || '#FF6B00') : 'rgba(0,0,0,0.06)', color: isSelected ? 'white' : style.color || 'var(--muted)' }}>
                    {optLabel}
                  </div>
                  <div style={{ fontSize: '14px', fontWeight: isSelected ? 700 : 500, flex: 1 }}>{option}</div>
                </div>
              );
            })}
          </div>
          {currentRevealed && (
            <div style={{ marginTop: '16px', background: '#E8F0FB', borderRadius: '8px', padding: '12px 16px', fontSize: '13px', color: 'var(--brand-secondary)', lineHeight: 1.6, fontWeight: 600 }}>
              💡 {currentQuestion.explanation || 'Good work checking your answer!'}
            </div>
          )}
          <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
            {isLastQuestion && currentRevealed ? (
              <button onClick={handleSubmit} disabled={submitting || !allRevealed}
                style={{ flex: 1, background: allRevealed ? '#1B7F3E' : '#E2E8F0', color: allRevealed ? 'white' : '#94A3B8', border: 'none', borderRadius: '10px', padding: '13px', fontSize: '14px', fontWeight: 800, cursor: allRevealed ? 'pointer' : 'not-allowed' }}>
                {submitting ? 'Submitting...' : `📊 ${t('quiz.submit')}`}
              </button>
            ) : (
              <button onClick={() => handleNext(currentQuestion.question_id)} disabled={!currentAnswered && !currentRevealed}
                style={{ flex: 1, background: currentAnswered || currentRevealed ? 'var(--brand-primary)' : '#E2E8F0', color: currentAnswered || currentRevealed ? 'white' : '#94A3B8', border: 'none', borderRadius: '10px', padding: '13px', fontSize: '14px', fontWeight: 800, cursor: currentAnswered || currentRevealed ? 'pointer' : 'not-allowed' }}>
                Next
              </button>
            )}
            {currentQ > 0 && (
              <button onClick={() => setCurrentQ(currentQ - 1)}
                style={{ background: 'white', color: 'var(--text)', border: '1.5px solid var(--border)', borderRadius: '10px', padding: '13px 18px', fontSize: '13px', fontWeight: 700, cursor: 'pointer' }}>
                ← Back
              </button>
            )}
          </div>
        </div>
        {!allRevealed && (
          <div style={{ background: '#FEF3C7', border: '1px solid #F59E0B', borderRadius: '10px', padding: '12px 16px', fontSize: '13px', color: '#7C2D12', fontWeight: 600, textAlign: 'center' }}>
            ⚠️ Answer all {totalQ} questions to submit. {totalQ - revealedCount} remaining.
          </div>
        )}
      </div>
    </AppShell>
  );
};

export default QuizPage;
